import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle2, FileCheck2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { TAX_QUIZ_KEY } from '../lib/quizResults';

const API = process.env.REACT_APP_BACKEND_URL;

const steps = [
  {
    key: 'status',
    label: 'Quelle est votre situation principale ?',
    options: [
      ['student', 'Etudiant'],
      ['employee', 'Salarie'],
      ['freelance', 'Freelance / independant'],
      ['mixed', 'Plusieurs activites'],
    ],
  },
  {
    key: 'changes',
    label: 'Avez-vous eu un changement important cette annee ?',
    options: [
      ['move', 'Demenagement'],
      ['job', 'Changement de travail'],
      ['family', 'Changement familial'],
      ['none', 'Aucun changement'],
    ],
  },
  {
    key: 'deductions',
    label: 'Avez-vous des frais ou reductions a verifier ?',
    options: [
      ['donations', 'Dons'],
      ['home_help', 'Emploi a domicile'],
      ['real_costs', 'Frais reels'],
      ['unknown', 'Je ne sais pas'],
    ],
  },
  {
    key: 'documents',
    label: 'Vos justificatifs sont-ils deja regroupes ?',
    options: [
      ['ready', 'Oui, tout est pret'],
      ['partial', 'Partiellement'],
      ['not_ready', 'Non'],
      ['unknown', 'Je ne sais pas quoi preparer'],
    ],
  },
];

const estimateTaxPrep = (answers) => {
  let score = 76;
  const warnings = [];
  const documents = ['Bulletin de paie de decembre', "Avis d'imposition precedent"];

  if (answers.status === 'freelance' || answers.status === 'mixed') {
    score -= 12;
    warnings.push('Revenus independants ou multi-activites a verifier');
    documents.push('Recapitulatif URSSAF ou chiffre d’affaires annuel');
  }

  if (answers.changes && answers.changes !== 'none') {
    score -= 8;
    warnings.push('Changement de situation a reporter correctement');
    documents.push('Justificatif lie au changement de situation');
  }

  if (answers.deductions && answers.deductions !== 'unknown') {
    score -= 7;
    warnings.push('Cases de reductions ou deductions a preparer');
    documents.push('Recu fiscal ou justificatif de frais');
  }

  if (answers.documents === 'partial') score -= 10;
  if (answers.documents === 'not_ready' || answers.documents === 'unknown') score -= 18;

  return {
    score: Math.max(32, score),
    warnings: warnings.length ? warnings : ['Situation simple, verification des cases pre-remplies conseillee'],
    documents: [...new Set(documents)],
  };
};

export const TaxPrepQuizPage = () => {
  const { isDark } = useTheme();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const showResult = stepIndex >= steps.length;
  const currentStep = showResult ? null : steps[stepIndex];
  const result = useMemo(() => estimateTaxPrep(answers), [answers]);

  const createAccount = () => {
    sessionStorage.setItem(TAX_QUIZ_KEY, JSON.stringify({ answers, result }));
    navigate('/register?source=tax-quiz');
  };

  const saveScore = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/score`, { answers }, { headers: { Authorization: `Bearer ${token}` } });
      sessionStorage.removeItem(TAX_QUIZ_KEY);
      navigate('/dashboard');
    } catch {
      toast.error("Impossible d'enregistrer ton score, réessaie.");
      setSaving(false);
    }
  };

  return (
    <div className={`min-h-screen px-5 py-8 ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#F8F7F4] text-[#1d1d1f]'}`}>
      <div className="max-w-3xl mx-auto">
        <Link to={token ? '/dashboard' : '/'} className={`inline-flex items-center gap-2 mb-8 text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
          <ArrowLeft size={17} />
          Retour
        </Link>

        <div className={`rounded-3xl border p-6 md:p-9 ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-xl'}`}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-[#81d8d0]/15 flex items-center justify-center">
              <FileCheck2 className="text-[#81d8d0]" size={25} />
            </div>
            <div>
              <p className="text-[#81d8d0] text-sm font-semibold">Pre-diagnostic impots</p>
              <p className={`text-sm ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                {token ? '4 questions, score enregistré sur ton compte' : '4 questions avant inscription, sans declaration automatique'}
              </p>
            </div>
          </div>

          {!showResult ? (
            <motion.div key={currentStep.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mb-6">
                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                  <div
                    className="h-full bg-[#81d8d0] transition-all duration-300"
                    style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }}
                  />
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-[#94A3B8]' : 'text-gray-500'}`}>
                  Question {stepIndex + 1}/{steps.length}
                </p>
              </div>

              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-7">{currentStep.label}</h1>

              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {currentStep.options.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAnswers((prev) => ({ ...prev, [currentStep.key]: value }))}
                    className={`text-left rounded-2xl border p-4 transition-all ${
                      answers[currentStep.key] === value
                        ? 'border-[#81d8d0] bg-[#81d8d0]/12'
                        : isDark
                          ? 'border-white/10 bg-white/5 hover:border-white/20'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setStepIndex((value) => value + 1)}
                disabled={!answers[currentStep.key]}
                className="btn-apple btn-apple-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {stepIndex === steps.length - 1 ? 'Voir mon score' : 'Continuer'}
                <ArrowRight size={18} className="ml-2" />
              </button>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <CheckCircle2 size={42} className="text-[#81d8d0] mb-5" />
              <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4">
                Votre situation administrative est optimisee a {result.score}%.
              </h1>
              <p className={`text-lg leading-8 mb-6 ${isDark ? 'text-[#94A3B8]' : 'text-gray-600'}`}>
                Skipya peut preparer la checklist, les documents et les points de vigilance avant votre declaration officielle.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <div className={`rounded-2xl p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <p className="text-sm font-semibold mb-3">Points de vigilance</p>
                  {result.warnings.map((item) => <p key={item} className="text-sm mb-2">- {item}</p>)}
                </div>
                <div className={`rounded-2xl p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <p className="text-sm font-semibold mb-3">Documents a preparer</p>
                  {result.documents.map((item) => <p key={item} className="text-sm mb-2">- {item}</p>)}
                </div>
              </div>
              {token ? (
                <button type="button" onClick={saveScore} disabled={saving} className="btn-apple btn-apple-primary disabled:opacity-40">
                  Enregistrer mon score et revenir à mon espace
                  <ArrowRight size={18} className="ml-2" />
                </button>
              ) : (
                <button type="button" onClick={createAccount} className="btn-apple btn-apple-primary">
                  Creer mon compte pour lancer le copilote
                  <ArrowRight size={18} className="ml-2" />
                </button>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxPrepQuizPage;
